import { useState, type Dispatch, type SetStateAction } from 'react'

import { Box, Card, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { CustomBreakFormType } from 'modules/time_categories_settings/lib/forms/CreateCustomBreakForm'

import Footer from './Footer'

interface Props {
  setModalOpen: Dispatch<SetStateAction<boolean>>
  form: FormikProps<CustomBreakFormType>
}

interface FormErrors {
  name: string
  paid: string
}

const BreakModal = ({ setModalOpen, form }: Props) => {
  const { values: formValues, errors: formErrors } = form
  const [missingError, setMissingError] = useState({
    name: false,
    paid: false,
  })

  const [breakPaid, setBreakPaid] = useState(form.values.breakTypeSelected)

  const onChangePaid = (value: boolean) => {
    setBreakPaid(true)
    setMissingError((prevState) => ({ ...prevState, paid: false }))
    form.setFieldValue('breakTypeSelected', true)
    form.setFieldValue('paid', value)
  }

  const onChangeName = (value: string) => {
    setMissingError((prevState) => ({ ...prevState, name: false }))
    form.setFieldValue('name', value)
  }

  const validateForm = (formValues: CustomBreakFormType) => {
    const errors: FormErrors = {
      name: !formValues.name ? i18n.t('company_settings.time.breaks.create.errors.name') : '',
      paid: !formValues.breakTypeSelected
        ? i18n.t('company_settings.time.breaks.create.errors.paid_type')
        : '',
    }
    return errors
  }

  const onSubmit = () => {
    const errors = validateForm(form.values)
    const hasErrors = Object.values(errors).some(Boolean)

    if (!hasErrors) {
      form.handleSubmit()
    } else {
      setMissingError({
        name: !!errors.name,
        paid: !!errors.paid,
      })
    }
  }

  const errorNameMessage =
    formErrors.name ||
    (missingError.name && i18n.t('company_settings.time.breaks.create.errors.name')) ||
    ''

  const errorPaidMessage =
    formErrors.paid ||
    (missingError.paid && i18n.t('company_settings.time.breaks.create.errors.paid_type')) ||
    ''

  const onClose = () => {
    form.resetForm()
    setMissingError({
      name: false,
      paid: false,
    })
    setModalOpen(false)
  }

  return (
    <Modal
      illustration="coffee"
      title={i18n.t('company_settings.time.breaks.create.title')}
      description={i18n.t('break_types.modals.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={<Footer onCancel={onClose} disabled={form.isSubmitting} />}
    >
      <Box gap="s24">
        <Box gap="s16">
          <Box gap="s4">
            <Text.Normal weight="medium" color="grey1000">
              {i18n.t('company_settings.time.breaks.create.name.title')}
            </Text.Normal>
            <TextField
              value={formValues.name}
              onChange={(value: string) => onChangeName(value)}
              error={errorNameMessage}
            />
          </Box>
        </Box>
        <Box gap="s16">
          <Box gap="s2">
            <Text.Normal weight="medium" color="grey1000">
              {i18n.t('company_settings.time.breaks.create.type.title')}
            </Text.Normal>
            <Text.Normal size="200">
              {i18n.t('company_settings.time.breaks.create.type.description')}
            </Text.Normal>
          </Box>

          <Box gap="s16">
            <Card onClick={() => onChangePaid(true)}>
              <Card.Content>
                <Box gap="s8" flexDirection="column">
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Text.Normal weight="semibold" color="grey1000">
                      {i18n.t('company_settings.time.breaks.create.paid_breaks.title')}
                    </Text.Normal>
                    <RadioButton
                      size="medium"
                      onChange={() => onChangePaid(true)}
                      value={(breakPaid && form.values.paid).toString()}
                      checked={breakPaid && form.values.paid}
                    />
                  </Box>
                  <Text.Normal weight="normal" color="grey800">
                    {i18n.t('company_settings.time.breaks.create.paid_breaks.description')}
                  </Text.Normal>
                </Box>
              </Card.Content>
            </Card>

            <Card onClick={() => onChangePaid(false)}>
              <Card.Content>
                <Box gap="s8" flexDirection="column">
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Text.Normal weight="semibold" color="grey1000">
                      {i18n.t('company_settings.time.breaks.create.unpaid_breaks.title')}
                    </Text.Normal>
                    <RadioButton
                      size="medium"
                      onChange={() => onChangePaid(false)}
                      value={(breakPaid && !form.values.paid).toString()}
                      checked={breakPaid && !form.values.paid}
                    />
                  </Box>
                  <Text.Normal weight="normal" color="grey800">
                    {i18n.t('company_settings.time.breaks.create.unpaid_breaks.description')}
                  </Text.Normal>
                </Box>
              </Card.Content>
            </Card>
            {missingError.paid && (
              <Text.Normal weight="normal" size="200" color="radicalRed1000">
                {errorPaidMessage}
              </Text.Normal>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default BreakModal
